<template>
    <div>
        <label
            for="promotional_code"
            class="text-sm text-gray-500"
        >
            {{ t('purchaseContract.promotionalCode') }}
        </label>

        <div class="flex items-center gap-2">
            <input-text
                id="promotional_code"
                v-model="promotionalCode"
                :placeholder="t('layout.enterCode')"
                :disabled="isLoading"
                @keydown.enter="handleApplyCode"
            />

            <prime-button
                :disabled="promotionalCode === ''"
                :loading="isLoading"
                :label="t('checkoutSections.apply')"
                @click="handleApplyCode"
            />
        </div>

        <div class="text-red-600 text-sm">
            {{ promoCodeError }}
        </div>

        <div class="mt-2 flex flex-col gap-2">
            <app-message
                v-for="(
                    appliedPromotionalCode, appliedPromotionalCodeIndex
                ) in modelValue"
                :key="appliedPromotionalCode.code"
                theme="success"
            >
                <div class="flex w-full items-center justify-between gap-4">
                    <div>
                        <span class="font-semibold">{{
                            appliedPromotionalCode.fullCode
                        }}</span>
                        <template
                            v-if="appliedPromotionalCode.description !== ''"
                        >
                            <br />
                            {{ appliedPromotionalCode.description }}
                        </template>
                    </div>

                    <button
                        type="button"
                        class="pi pi-times"
                        :aria-label="
                            t('purchaseContract.removePromotionalCode')
                        "
                        @click="handleRemoveCode(appliedPromotionalCodeIndex)"
                    />
                </div>
            </app-message>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import InputText from 'primevue/inputtext'
import PrimeButton from 'primevue/button'
import AppMessage from '~/components/AppMessage.vue'
import type { AppliedPromotionalCode } from '~/components/PurchaseContract/promotional-code'
import { useRpQuery } from '~/composables/graphql'
import { graphql } from '~/resources/graphql'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useViewer } from '#imports'
import { useRoute } from '#app'
import { useRouter } from 'vue-router'

import { useSelectedLanguage } from '~/composables/use-selected-language'
import { useI18n } from 'vue-i18n'
const props = defineProps<{
    modelValue: AppliedPromotionalCode[]
    planId: string
}>()

const emit = defineEmits<{
    (e: 'update:modelValue', payload: AppliedPromotionalCode[]): void
}>()

const activeFacility = useActiveFacility()
const { t } = useI18n({ useScope: 'global' })
const rpQuery = useRpQuery()
const selectedLanguage = useSelectedLanguage()
const promotionalCode = ref<string>('')
const lookupPromotionalCode = ref<string>('')
const viewer = useViewer()
const customerId = computed(() => viewer.value?.customerId ?? null)

const isLoading = ref(false)
const promoCodeError = ref('')

async function handleApplyCode() {
    if (isLoading.value === true) {
        return
    }

    lookupPromotionalCode.value = promotionalCode.value

    // Nothing to lookup
    if (lookupPromotionalCode.value === '') {
        return
    }

    if (
        props.modelValue.some(
            data =>
                data.fullCode.toLowerCase() ===
                lookupPromotionalCode.value.toLowerCase()
        )
    ) {
        promoCodeError.value = t('purchaseContract.codeAlreadyAdded')
        ;(
            document.getElementById('promotional_code') as HTMLInputElement
        )?.select?.()
        return
    }

    try {
        const response = await lookUpCode(lookupPromotionalCode.value)

        if (
            response.promotionCodeLookup.__typename ===
            'PromotionCodeLookupSuccess'
        ) {
            promotionalCode.value = ''
            lookupPromotionalCode.value = ''
            emit('update:modelValue', [
                ...props.modelValue,
                {
                    code: response.promotionCodeLookup.promotionCode.code,
                    fullCode: response.promotionCodeLookup.fullCode,
                    description:
                        response.promotionCodeLookup.promotionCode.promotion
                            .translatedDescription
                }
            ])
        }
        // Promo code not found
        else {
            promoCodeError.value = response.promotionCodeLookup.message
            ;(
                document.getElementById('promotional_code') as HTMLInputElement
            )?.select?.()
        }
    } finally {
        isLoading.value = false
    }
}

// When input box changes, remove error code
watch(promotionalCode, () => {
    promoCodeError.value = ''
})

async function handleRemoveCode(appliedPromotionalCodeIndex: number) {
    emit('update:modelValue', [
        ...props.modelValue.slice(0, appliedPromotionalCodeIndex),
        ...props.modelValue.slice(appliedPromotionalCodeIndex + 1)
    ])
}

const route = useRoute()
const router = useRouter()
onMounted(async () => {
    const routeCode = route.query.promo
    const routeCodes = (
        Array.isArray(routeCode) ? routeCode : [routeCode]
    ).filter(
        (code): code is string =>
            code !== undefined && code !== null && code !== ''
    )

    if (routeCodes.length > 0) {
        let appliedPromotionalCodes = [...props.modelValue]
        const errors: string[] = []
        const requestedCodes = new Set<string>()
        const lookupPromises: Array<
            Promise<{
                code: string
                response: Awaited<ReturnType<typeof lookUpCode>>
            }>
        > = []

        for (const code of routeCodes) {
            if (
                appliedPromotionalCodes.some(
                    data => data.fullCode.toLowerCase() === code.toLowerCase()
                )
            ) {
                errors.push(
                    code + ': ' + t('purchaseContract.codeAlreadyAdded')
                )
                continue
            }

            if (requestedCodes.has(code.toLowerCase())) {
                continue
            }
            requestedCodes.add(code.toLowerCase())

            lookupPromises.push(
                lookUpCode(code).then(response => ({
                    code,
                    response
                }))
            )
        }

        const lookupResults = await Promise.all(lookupPromises)
        for (const lookupResult of lookupResults) {
            const code = lookupResult.code
            const promotionCodeLookup =
                lookupResult.response.promotionCodeLookup
            if (
                promotionCodeLookup.__typename === 'PromotionCodeLookupSuccess'
            ) {
                if (
                    appliedPromotionalCodes.some(
                        data =>
                            data.fullCode.toLowerCase() ===
                            promotionCodeLookup.fullCode.toLowerCase()
                    )
                ) {
                    continue
                }

                appliedPromotionalCodes = [
                    ...appliedPromotionalCodes,
                    {
                        code: promotionCodeLookup.promotionCode.code,
                        fullCode: promotionCodeLookup.fullCode,
                        description:
                            promotionCodeLookup.promotionCode.promotion
                                .translatedDescription
                    }
                ]
            } else {
                errors.push(code + ': ' + promotionCodeLookup.message)
            }
        }

        if (appliedPromotionalCodes.length !== props.modelValue.length) {
            emit('update:modelValue', appliedPromotionalCodes)
        }

        if (errors.length > 0) {
            promoCodeError.value = errors.join('; ')
            ;(
                document.getElementById('promotional_code') as HTMLInputElement
            )?.select?.()
        }

        // Remove promo from URL
        await router.replace({
            ...route,
            query: {
                ...route.query,
                promo: undefined
            }
        })
    }
})

async function lookUpCode(code: string) {
    return await rpQuery(
        graphql(`
            query PromotionalCodeLookupQuery(
                $filter: PromotionCodeLookupFilter!
                $language: Language!
            ) {
                promotionCodeLookup(filter: $filter) {
                    __typename
                    ... on PromotionCodeLookupError {
                        message
                    }
                    ... on PromotionCodeLookupSuccess {
                        fullCode
                        promotionCode {
                            id
                            code
                            promotion {
                                translatedDescription(language: $language)
                            }
                        }
                    }
                }
            }
        `),
        {
            filter: {
                code: code,
                facilityId: activeFacility.value.id,
                planId: props.planId,
                customerId: customerId.value
            },
            language: selectedLanguage.value
        }
    )
}
</script>
